// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { LazyImageStyle } from "@turnerBlueprint/MobiusOverrides/LazyImage";
import { StyledIcon } from "@turnerBlueprint/MobiusOverrides/Link/Link";
import { DEFAULT_MENU_WIDTH } from "@turnerBlueprint/MobiusOverrides/Menu/Menu";
import { mainNavigationStyles } from "@turnerBlueprint/Overrides/Widgets/Header/MainNavigation";
import { constants } from "@turnerBlueprint/XDL/constants";
import IconArrowDown from "@turnerBlueprint/XDL/Icons/IconArrowDown";
import IconArrowRight from "@turnerBlueprint/XDL/Icons/IconArrowRight";
import { fontConfig } from "@turnerBlueprint/XDL/PreStyle/typography";
import { color, createLinkIconStyles, getSystemColor, layoutContainer } from "@turnerBlueprint/XDL/Utilities";
import { css } from "styled-components";

mainNavigationStyles.container = {
    css: css`
        display: flex;
        justify-content: left;
        ${layoutContainer}
    `,
};

mainNavigationStyles.menuItem = {
    css: css`
        display: flex;
        align-items: center;
        height: 43px;
        padding: 12px 32px;

        ${TypographyStyle} {
            display: flex;
            align-items: center;
        }

        &&& {
            ${StyledIcon} {
                color: ${color("common.ui1", 0.5)};
            }
            &:focus {
                outline: none;
            }

            &:hover {
                background-color: ${color("secondary")};

                ${TypographyStyle} {
                    color: ${BLUEPRINT_NAME === "turnerBlueprint" ? color("common.ui7") : color("common.ui1")};
                }
                ${StyledIcon} {
                    color: ${BLUEPRINT_NAME === "turnerBlueprint"
                        ? color("common.ui7", 0.5)
                        : color("common.ui3", 0.5)};
                    transform: rotate(180deg);
                }
            }
        }
    `,
};

mainNavigationStyles.itemWrapper = {
    css: css`
        position: relative;
        overflow: visible;
        display: flex;
        &:hover span + div {
            display: block;
        }
    `,
};

mainNavigationStyles.menuItemTypography = {
    size: 16,
    lineHeight: "19px",
    weight: fontConfig.weight.bold,
    css: css`
        &&& {
            color: ${getSystemColor("common.ui1")};
        }
    `,
};

mainNavigationStyles.menuItemIcon = {
    src: IconArrowDown,
    color: "secondary",
    size: 8,
    css: css`
        margin-left: 8px;
        margin-right: 0;
        margin-top: 1px;
        svg {
            height: auto;
        }
    `,
};

mainNavigationStyles.cascadingMenu = {
    cssOverrides: {
        contentWrapper: {
            css: css`
                background-color: ${color("common.ui1")};
                position: absolute;
                left: 100%;
                top: 0;
                padding: 12px;
                height: 100%;
                max-height: calc(calc(100vh - ${constants.header.size.height}px) * 0.8);
                width: ${DEFAULT_MENU_WIDTH}px;
                display: flex;
                flex-direction: column;
                box-shadow: 1px 0 0 0 ${color("common.ui4")};
                margin-left: 1px;
                ${LazyImageStyle} {
                    justify-content: start;
                }
            `,
        },
        contentTitle: {
            variant: "h4",
            css: css`
                margin-bottom: 8px;
            `,
        },
        contentText: {
            variant: "h6",
            weight: fontConfig.weight.regular,
            css: css`
                line-height: 22px;
                margin-bottom: 16px;
            `,
        },
        contentLink: {
            ...createLinkIconStyles(
                IconArrowRight,
                {
                    css: css`
                        margin-bottom: 24px;
                        justify-content: flex-end;
                        width: fit-content;
                    `,
                },
                {
                    size: 15,
                    lineHeight: "18px",
                    weight: fontConfig.weight.bold,
                },
                {
                    size: 6,
                    css: css`
                        &,
                        & svg {
                            height: auto;
                        }
                    `,
                },
                "right",
            ),
        },
        contentImage: {
            width: "100%",
            mergeCss: true,
            css: css`
                max-height: 400px;
                object-fit: contain;
            `,
        },
    },
};

mainNavigationStyles.mobileMenuWrapper = {
    css: css`
        height: 16px;
    `,
};
